import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { userLoginSagas } from '../../../ducks/user'

// Components
import LoginForm from '../../components/Forms/Login'
import Logo from '../../components/UI/Logo'
import { Button } from '../../components/UI/Buttons'
import { useNotification } from 'lib/context/notification.context'
// selector
import { errorSelector } from '../../../ducks/user'
// actions
import { setError, setUser } from 'ducks/user'
import { setAuth } from 'ducks/auth'

import { useTranslation } from 'react-i18next'
import {
  Container,
  LoginActionRow,
  LoginDescription,
  LoginEyebrow,
} from './styled'
import { IOS_DEVICE, SESSION_TIMEOUT_MESSAGE_KEY, TOKEN } from 'constants/global.constants'
import axios from 'axios'

const iOS = IOS_DEVICE.test(navigator.userAgent)

interface LoginFormContainerProps {
  variant?: 'admin' | 'evaluated'
}

const LoginFormContainer: FC<LoginFormContainerProps> = ({ variant = 'admin' }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const error = useSelector(errorSelector)

  const { getWarning } = useNotification()
  const isEvaluated = variant === 'evaluated'
  const [ssoRedirecting, setSsoRedirecting] = useState(false)

  const handleResize = () => {
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    if (iOS) {
      window.addEventListener('resize', handleResize)

      handleResize()

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [])

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search)
    const ssoError = parameters.get('sso_error')
    if (ssoError === 'MICROSOFT_ACCOUNT_MISMATCH') {
      getWarning('La cuenta Microsoft seleccionada no coincide con el correo indicado. Vuelva a intentarlo con la cuenta correcta.')
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (ssoError === 'BLUESITE_USER_NOT_REGISTERED') {
      getWarning('La cuenta existe en Microsoft Entra, pero no está registrada en Bluesite o no tiene permisos para acceder. Contacte con su empresa para solicitar el alta o los permisos correspondientes.')
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (ssoError === 'BLUESITE_USER_INACTIVE') {
      getWarning('La cuenta existe en Bluesite, pero está inactiva. Contacte con su empresa para solicitar la activación.')
      window.history.replaceState({}, document.title, window.location.pathname)
    }
    const token = parameters.get('sso_token')
    if (!token) return
    const companyId = Number(parameters.get('companyId') || 0)
    const userName = parameters.get('userName') || ''
    const email = parameters.get('email') || ''
    const hasAdminPlatformManagement = parameters.get('hasAdminPlatformManagement') === 'true'
    const hasQuestionnaireManagement = parameters.get('hasQuestionnaireManagement') === 'true'
    const roleType = JSON.parse(atob(token.split('.')[1])).roleType
    localStorage.setItem(TOKEN, token)
    dispatch(setUser({ token, email, companyId, userName, hasAdminPlatformManagement, hasQuestionnaireManagement }))
    dispatch(setAuth({ role: roleType, isAuth: true }))
    navigate('/', { replace: true })
  }, [dispatch, getWarning, navigate])

  const handleLogin = async (values: any): Promise<void> => {
    const email = values.email.toLowerCase()
    if (!isEvaluated) {
      const response = await axios.post('/auth/login/microsoft/start', { email })
      if (response.data.status === 'SSO_REQUIRED') {
        setSsoRedirecting(true)
        window.setTimeout(() => window.location.assign(response.data.authorization_url), 150)
        return
      }
    }
    if (!values.password) {
      getWarning('Introduce tu contraseña para acceder a Bluesite.')
      return
    }
    dispatch(userLoginSagas.run({ email, password: values.password }))
  }

  useEffect(() => {
    dispatch(setError(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timeoutMessage: string | null = sessionStorage.getItem(SESSION_TIMEOUT_MESSAGE_KEY)

    if (timeoutMessage) {
      getWarning(timeoutMessage)
      sessionStorage.removeItem(SESSION_TIMEOUT_MESSAGE_KEY)
    }
  }, [getWarning])

  useEffect(() => {
    if (error) {
      if (error.code === '401') {
        getWarning(t('global.notifications.loginEmailOrPasswordErrorMessage'))
      } else if (error.code === '421') {
        getWarning(t('global.notifications.loginInactiveUserErrorMessage'))
      } else if (error.code === '420') {
        getWarning(t('global.notifications.loginEmailNotMainErrorMessage'))
      } else if (error.code === '422') {
        getWarning(t('global.notifications.deactivateAccountErrorMessage'))
      } else if (error.code === 'MFA_EMAIL_DELIVERY_FAILED') {
        getWarning(error.message)
      } else if (error.message) {
        getWarning(error.message)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, getWarning])

  return (
    <Container>
      <Logo width="245px" />
      <LoginEyebrow>{isEvaluated ? 'Acceso para evaluados' : 'Acceso seguro para administradores'}</LoginEyebrow>
      <LoginDescription>
        {isEvaluated
          ? 'Ingresa para contestar tus pruebas en una experiencia más clara, enfocada y fácil de seguir.'
          : 'Ingresa para gestionar talento, procesos, analítica y desarrollo con una experiencia más sólida.'}
      </LoginDescription>
      <LoginForm onSubmit={(e) => handleLogin(e)} ssoRedirecting={ssoRedirecting} />
      {ssoRedirecting && <LoginDescription>Estamos conectando de forma segura con Microsoft Entra.</LoginDescription>}
      <LoginActionRow>
        {isEvaluated ? (
          <Button variant="ghost" size="md" onClick={() => navigate('/login')}>
            Volver al acceso de administración
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="md"
            onClick={() => navigate('/login/evaluated')}
            background="linear-gradient(180deg, #F2F9EC 0%, #E9F7DD 100%)"
            color="#34651A"
            border="1px solid #BEE1A6"
          >
            Voy a contestar pruebas
          </Button>
        )}
      </LoginActionRow>
    </Container>
  )
}

export default LoginFormContainer
